import { isRecord } from '~/utils/isRecord';

import { FACILITY_REPLAY_CODES, REPLAY_CODE_MESSAGES, TECHNICAL_REPLAY_DETAIL_KEYS } from './constants';
import type { FacilityValidationIssue } from './types';

function isValidationResult(value: unknown): value is { valid: boolean; issues: Array<unknown> } {
  return isRecord(value) && typeof value.valid === 'boolean' && Array.isArray(value.issues);
}

function asValidationIssues(value: unknown): Array<FacilityValidationIssue> {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((issue) => {
    if (!isRecord(issue) || typeof issue.code !== 'string' || typeof issue.message !== 'string') {
      return [];
    }

    return [
      {
        code: issue.code,
        message: issue.message,
        facilityCode: typeof issue.facilityCode === 'string' ? issue.facilityCode : undefined,
        path: typeof issue.path === 'string' ? issue.path : undefined,
      },
    ];
  });
}

export function getValidationIssuesFromDetail(source: unknown): Array<FacilityValidationIssue> {
  if (!isRecord(source)) {
    return [];
  }

  const directIssues = Array.isArray(source.issues) ? asValidationIssues(source.issues) : [];
  if (directIssues.length > 0) {
    return directIssues;
  }

  if (isValidationResult(source.result)) {
    return asValidationIssues(source.result.issues);
  }

  return [];
}

export function summarizeFacilityValidationIssues(issues: Array<FacilityValidationIssue>): string | undefined {
  if (issues.length === 0) {
    return undefined;
  }

  const issueSummaries = issues.map((issue) => {
    const facilityLabel = issue.facilityCode ? ` (${issue.facilityCode})` : '';

    switch (issue.code) {
      case 'parameterized_value_required':
        return `A facility requires a numeric value${facilityLabel}.`;
      case 'facility_not_found':
        return `A facility no longer exists in the catalog${facilityLabel}.`;
      case 'duplicate_facility_code':
        return `A facility appears more than once${facilityLabel}.`;
      default:
        return issue.message;
    }
  });

  const uniqueSummaries = Array.from(new Set(issueSummaries));
  return `This facility change could not be applied: ${uniqueSummaries.join(' ')} Ask the partner to review their facilities in Extranet and submit a corrected change.`;
}

export function inferReplayCodeFromDetail(source: unknown): string | undefined {
  if (!isRecord(source)) {
    return undefined;
  }

  const upstreamCode = typeof source.code === 'string' ? source.code : undefined;
  if (upstreamCode === 'premise_mismatch') {
    return FACILITY_REPLAY_CODES.PREMISE_MISMATCH;
  }

  const validationIssues = getValidationIssuesFromDetail(source);
  if (validationIssues.length === 0) {
    return undefined;
  }

  if (validationIssues.some((issue) => issue.code === 'facility_not_found')) {
    return FACILITY_REPLAY_CODES.NOT_FOUND;
  }

  return FACILITY_REPLAY_CODES.VALIDATION_FAILED;
}

export function mapUpstreamCodeToReplayCode(upstreamCode?: string): string | undefined {
  if (upstreamCode === 'premise_mismatch') {
    return FACILITY_REPLAY_CODES.PREMISE_MISMATCH;
  }

  return undefined;
}

export function getMessageForReplayCode(replayCode?: string): string | undefined {
  if (!replayCode) {
    return undefined;
  }
  return REPLAY_CODE_MESSAGES[replayCode];
}

export function hasReplayFailureSignals(body: Record<string, unknown>): boolean {
  if (
    body.replayError !== undefined ||
    body.replayDetail !== undefined ||
    body.agentMessage !== undefined ||
    body.replayCode !== undefined
  ) {
    return true;
  }

  if (body.code === 'premise_mismatch') {
    return true;
  }

  const status = typeof body.status === 'number' ? body.status : undefined;
  if (status && status >= 400 && status < 500 && getValidationIssuesFromDetail(body).length > 0) {
    return true;
  }

  if (isValidationResult(body.result) || getValidationIssuesFromDetail(body).length > 0) {
    return true;
  }

  return false;
}

export function extractTechnicalReplayDetail(body: Record<string, unknown>): unknown {
  if (body.replayDetail !== undefined) {
    return body.replayDetail;
  }

  const technical = TECHNICAL_REPLAY_DETAIL_KEYS.reduce<Record<string, unknown>>((accumulator, key) => {
    if (body[key] !== undefined) {
      accumulator[key] = body[key];
    }
    return accumulator;
  }, {});

  if (isRecord(body.result)) {
    technical.result = body.result;
  }

  return Object.keys(technical).length > 0 ? technical : undefined;
}
